<template>
  <div id="tournament-ranking-section" data-qe-id="ranking-section">
    <ul class="t-table-tabs">
      <li
        v-if="hasTournamentId"
        :class="hasSelectionState(TABLE_TOURNAMENT)"
        @click="tableToShow = TABLE_TOURNAMENT"
        data-qe-id="table-selection-tab--current"
      >
        {{ tabLabelTournament }}
      </li>
      <li
        v-if="isMasters"
        :class="hasSelectionState(TABLE_SEASON)"
        @click="tableToShow = TABLE_SEASON"
        data-qe-id="table-selection-tab--masters"
      >
        {{ mastersTabLabel }}
      </li>
      <li
        :class="hasSelectionState(TABLE_ZONE_HISTORY)"
        @click="tableToShow = TABLE_ZONE_HISTORY"
        data-qe-id="table-selection-tab--history"
      >
        {{ tabLabelHistory }}
      </li>
    </ul>
    <m-season-table
      v-if="isMasters && tableToShow === TABLE_SEASON"
      :zone-id="store.state.zone.zoneId"
      data-qe-id="ranking-table--masters"
    />
    <m-tournament-table v-if="tableToShow === TABLE_TOURNAMENT" :t-id="tid" data-qe-id="ranking-table--current" />
    <m-zone-history-table
      v-if="tableToShow === TABLE_ZONE_HISTORY"
      :zone-id="store.state.zone.zoneId"
      data-qe-id="ranking-table--history"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/store'
import MSeasonTable from '@/modules/MSeasonTable.vue'
import MTournamentTable from '@/modules/MTournamentTable.vue'
import MZoneHistoryTable from '@/modules/MZoneHistoryTable.vue'
import { TOURNAMENT_TYPE, TournamentTypeToName } from '@/model/TournamentType'

enum TableType {
  SEASON = 'ranking.tabletype.season',
  TOURNAMENT = 'ranking.tabletype.tournament',
  ZONE_HISTORY = 'ranking.tabletype.zoneHistory',
}

const route = useRoute()
const store = useStore()
const { t } = useI18n()

const TABLE_SEASON = TableType.SEASON
const TABLE_TOURNAMENT = TableType.TOURNAMENT
const TABLE_ZONE_HISTORY = TableType.ZONE_HISTORY

const tableToShow = ref(TABLE_ZONE_HISTORY)

const tabLabelHistory = computed(() => {
  return isMasters.value ? t('table.type.zone_history') : t('table.type.onetime_history')
})

const tabLabelTournament = computed(() => {
  return isMasters.value ? t('table.type.tournament_weekly') : t('table.type.onetime')
})

const isMasters = computed(() => {
  return (
    store.state.zone.zoneId === TournamentTypeToName[TOURNAMENT_TYPE.SKA_MASTERS] ||
    store.state.zone.zoneId === TournamentTypeToName[TOURNAMENT_TYPE.SKA_MASTERS_GD] ||
    store.state.zone.zoneId === TournamentTypeToName[TOURNAMENT_TYPE.ROM_MASTERS] ||
    store.state.zone.zoneId === TournamentTypeToName[TOURNAMENT_TYPE.ROM_MASTERS_GD]
  )
})

const mastersTabLabel = computed(() => {
  const label = t('table.type.masters_season') as string
  const game = t(`generic.game.name_${store.state.zone.gametype}`) as string
  return label.replace('{gametype}', game)
})

const hasTournamentId = computed(() => {
  return !!store.state.tournament.tournamentId
})

const tid = computed(() => {
  return store.state.tournament.tournamentId
})

onMounted(() => {
  // find out which table has to be preselected
  if (route.query.table === 'history') {
    // the query has first prio - the others I don't know
    tableToShow.value = TABLE_ZONE_HISTORY
    document.getElementById('tournament-ranking-section').scrollIntoView({ behavior: 'smooth' })
  } else if (hasTournamentId.value) {
    tableToShow.value = TABLE_TOURNAMENT
  } else if (isMasters.value) {
    tableToShow.value = TABLE_SEASON
  }
})

function hasSelectionState(state: TableType) {
  return tableToShow.value === state ? 't-table-tabs-active' : ''
}
</script>
<style lang="less">
.search-not-found,
.qualified-list-end-row,
.no-results {
  text-align: center;
}
</style>
